<template>
  <div id="group1">
    <h4 class="section-title">
      Top Picks
    </h4>

    <div class="preset-regimen-box">
      <span class="preset-result-count">
        Showing {{ physicians.length }} results
      </span>

      <div>
        <div
          v-for="physician in physicians"
          :key="physician.id"
          :id="`top-pick-card-${physician.id}`"
          class="preset-regimen-card"
        >
          <!-- Physician / SP header -->
          <div class="preset-regimen-header">
            <div class="preset-regimen-meta">
              <a
                class="preset-regimen-name"
                href="#"
                @click.prevent="showProducts(physician)"
              >
                {{ physician.name }}

                <svg
                  :class="[
                    'preset-chevron',
                    {
                      'preset-chevron--open': physician.showProducts
                    }
                  ]"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </a>

              <span class="preset-regimen-info">
                {{ physician.products.length }} products

                <span v-if="physician.practice">
                  &nbsp;·&nbsp; {{ physician.practice.name }}
                </span>
              </span>
            </div>

            <!-- Bulk AM / PM / Both -->
            <div class="preset-bulk-actions">
              <!-- AM -->
              <button
                class="time-btn"
                title="Add all to Morning"
                :disabled="isPhysicianAllAdded(physician, 'am')"
                :class="{
                  disabled: isPhysicianAllAdded(physician, 'am')
                }"
                @click="handleProducts(physician.products, 'am')"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="12" cy="12" r="4"/>
                  <line x1="12" y1="2" x2="12" y2="4"/>
                  <line x1="12" y1="20" x2="12" y2="22"/>
                  <line
                    x1="4.22"
                    y1="4.22"
                    x2="5.64"
                    y2="5.64"
                  />
                  <line
                    x1="18.36"
                    y1="18.36"
                    x2="19.78"
                    y2="19.78"
                  />
                  <line x1="2" y1="12" x2="4" y2="12"/>
                  <line x1="20" y1="12" x2="22" y2="12"/>
                  <line
                    x1="4.22"
                    y1="19.78"
                    x2="5.64"
                    y2="18.36"
                  />
                  <line
                    x1="18.36"
                    y1="5.64"
                    x2="19.78"
                    y2="4.22"
                  />
                </svg>

                <span>AM</span>
              </button>

              <!-- PM -->
              <button
                class="time-btn"
                title="Add all to Evening"
                :disabled="isPhysicianAllAdded(physician, 'pm')"
                :class="{
                  disabled: isPhysicianAllAdded(physician, 'pm')
                }"
                @click="handleProducts(physician.products, 'pm')"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path
                    d="M21 12.79A9 9 0 1 1 11.21 3
                    7 7 0 0 0 21 12.79z"
                  />
                </svg>

                <span>PM</span>
              </button>

              <!-- Both -->
              <button
                class="time-btn time-btn--both"
                title="Add all to Both AM & PM"
                :disabled="isPhysicianAllAdded(physician, 'both')"
                :class="{
                  disabled: isPhysicianAllAdded(physician, 'both')
                }"
                @click="handleProducts(physician.products, 'both')"
              >
                <!-- AM -->
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="12" cy="12" r="4"/>
                  <line x1="12" y1="2" x2="12" y2="4"/>
                  <line x1="12" y1="20" x2="12" y2="22"/>
                  <line
                    x1="4.22"
                    y1="4.22"
                    x2="5.64"
                    y2="5.64"
                  />
                  <line
                    x1="18.36"
                    y1="18.36"
                    x2="19.78"
                    y2="19.78"
                  />
                  <line x1="2" y1="12" x2="4" y2="12"/>
                  <line x1="20" y1="12" x2="22" y2="12"/>
                  <line
                    x1="4.22"
                    y1="19.78"
                    x2="5.64"
                    y2="18.36"
                  />
                  <line
                    x1="18.36"
                    y1="5.64"
                    x2="19.78"
                    y2="4.22"
                  />
                </svg>

                <!-- PM -->
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path
                    d="M21 12.79A9 9 0 1 1 11.21 3
                    7 7 0 0 0 21 12.79z"
                  />
                </svg>

                <span>Both</span>
              </button>
            </div>
          </div>

          <!-- Products -->
          <div
            v-if="physician.showProducts"
            class="preset-products-list"
          >
            <div
              v-for="product in physician.products"
              :key="product.id"
              class="product-wrapper"
              draggable="true"
              @dragstart="startDrag($event, product)"
            >
              <div class="product-item">
                <div class="product-info-wrapper">
                  <img
                    :src="productImage(product)"
                    :alt="product.title"
                    class="product-image"
                  >

                  <div class="product-details">
                    <p class="product-name">
                      {{ product.title }}
                    </p>

                    <p class="product-vendor">
                      {{ product.vendor }}
                    </p>
                  </div>
                </div>

                <div class="product-actions">
                  <a
                    href="#"
                    class="btn-view"
                    @click.prevent="
                      toggleProductDetail(product.id)
                    "
                  >
                    {{
                      expandedProduct === product.id
                        ? 'Close Details'
                        : 'View Details'
                    }}
                  </a>

                  <div class="time-actions">
                    <!-- AM -->
                    <button
                      class="time-btn"
                      title="Morning"
                      :disabled="isInMorning(product)"
                      :class="{
                        disabled: isInMorning(product)
                      }"
                      @click="emitProduct(product, 'am')"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.8"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <circle cx="12" cy="12" r="4"/>
                        <line
                          x1="12"
                          y1="2"
                          x2="12"
                          y2="4"
                        />
                        <line
                          x1="12"
                          y1="20"
                          x2="12"
                          y2="22"
                        />
                        <line
                          x1="4.22"
                          y1="4.22"
                          x2="5.64"
                          y2="5.64"
                        />
                        <line
                          x1="18.36"
                          y1="18.36"
                          x2="19.78"
                          y2="19.78"
                        />
                        <line
                          x1="2"
                          y1="12"
                          x2="4"
                          y2="12"
                        />
                        <line
                          x1="20"
                          y1="12"
                          x2="22"
                          y2="12"
                        />
                        <line
                          x1="4.22"
                          y1="19.78"
                          x2="5.64"
                          y2="18.36"
                        />
                        <line
                          x1="18.36"
                          y1="5.64"
                          x2="19.78"
                          y2="4.22"
                        />
                      </svg>
                    </button>

                    <!-- PM -->
                    <button
                      class="time-btn"
                      title="Evening"
                      :disabled="isInEvening(product)"
                      :class="{
                        disabled: isInEvening(product)
                      }"
                      @click="emitProduct(product, 'pm')"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.8"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path
                          d="M21 12.79A9 9 0 1 1 11.21 3
                          7 7 0 0 0 21 12.79z"
                        />
                      </svg>
                    </button>

                    <!-- Both -->
                    <button
                      class="time-btn time-btn--both"
                      title="Both AM & PM"
                      :disabled="
                        isInMorning(product) &&
                        isInEvening(product)
                      "
                      :class="{
                        disabled:
                          isInMorning(product) &&
                          isInEvening(product)
                      }"
                      @click="emitProduct(product, 'both')"
                    >
                      <!-- AM -->
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.8"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <circle cx="12" cy="12" r="4"/>
                        <line
                          x1="12"
                          y1="2"
                          x2="12"
                          y2="4"
                        />
                        <line
                          x1="12"
                          y1="20"
                          x2="12"
                          y2="22"
                        />
                        <line
                          x1="4.22"
                          y1="4.22"
                          x2="5.64"
                          y2="5.64"
                        />
                        <line
                          x1="18.36"
                          y1="18.36"
                          x2="19.78"
                          y2="19.78"
                        />
                        <line
                          x1="2"
                          y1="12"
                          x2="4"
                          y2="12"
                        />
                        <line
                          x1="20"
                          y1="12"
                          x2="22"
                          y2="12"
                        />
                        <line
                          x1="4.22"
                          y1="19.78"
                          x2="5.64"
                          y2="18.36"
                        />
                        <line
                          x1="18.36"
                          y1="5.64"
                          x2="19.78"
                          y2="4.22"
                        />
                      </svg>

                      <!-- PM -->
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.8"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path
                          d="M21 12.79A9 9 0 1 1 11.21 3
                          7 7 0 0 0 21 12.79z"
                        />
                      </svg>
                    </button>

                    <span
                      v-if="
                        isInMorning(product) &&
                        isInEvening(product)
                      "
                      class="product-added-badge"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#5eb052"
                        stroke-width="2.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <polyline
                          points="20 6 9 17 4 12"
                        />
                      </svg>

                      Added
                    </span>
                  </div>
                </div>
              </div>

              <!-- Description -->
              <div
                v-if="expandedProduct === product.id"
                class="product-description"
              >
                <div
                  v-if="product.descriptionHtml"
                  class="description-content"
                >
                  <h5>Description:</h5>

                  <div
                    class="description-text"
                    v-html="product.descriptionHtml"
                  />
                </div>

                <div
                  v-else
                  class="description-content"
                >
                  <p class="no-description">
                    No description available for this product.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  /* ── SECTION WRAPPER ─────────────────────────────────── */
  .preset-regimen-box {
    padding: 0 10px;
  }

  .preset-result-count {
    display: block;
    font-size: 13px;
    color: #6b7280;
    margin-bottom: 12px;
    padding: 0 7px;
  }

  /* ── REGIMEN CARD ────────────────────────────────────── */
  .preset-regimen-card {
    border: 1px solid #EBEBEB;
    border-radius: 12px;
    background: #fff;
    margin-bottom: 8px;
    overflow: hidden;
    transition: box-shadow .18s, border-color .18s;

    &:hover {
      border-color: #9F7ED9;
      box-shadow: 0 4px 18px rgba(159, 126, 217, .13);
    }
  }

  .preset-regimen-header {
    padding: 14px 20px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .preset-bulk-actions {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    padding-top: 2px;
  }

  .preset-bulk-actions .time-btn {
    width: auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    background: #F9F9F9;
    border: 1px solid #E4E4E4;
    border-radius: 8px;
    padding: 5px 10px;
    cursor: pointer;
    color: #374151;
    font-size: 12px;
    font-weight: 500;
    transition: border-color .15s, color .15s, background .15s;
    line-height: 1;
    white-space: nowrap;

    &:hover:not(:disabled) {
      border-color: #9F7ED9;
      color: #9F7ED9;
      background: #f5f0fd;
    }

    &.disabled,
    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
      pointer-events: none;
      background: #f5f5f5;
    }
  }

  .preset-bulk-actions .time-btn--both {
    gap: 3px;
  }

  .preset-regimen-name {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 15px;
    font-weight: 700;
    color: #111827;
    text-decoration: none;
    margin-bottom: 4px;

    &:hover {
      color: #9F7ED9;
    }
  }

  .preset-chevron {
    flex-shrink: 0;
    transition: transform .2s ease;
    color: #9ca3af;

    &--open {
      transform: rotate(180deg);
    }
  }

  .preset-regimen-info {
    display: block;
    font-size: 13px;
    color: #6b7280;
    margin-bottom: 6px;
  }

  .preset-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 6px;
  }

  .preset-tag {
    display: inline-block;
    background: #f3effd;
    color: #7c52c8;
    font-size: 12px;
    font-weight: 500;
    padding: 2px 10px;
    border-radius: 999px;
  }

  .preset-description {
    font-size: 13px;
    color: #374151;
    margin: 4px 0 0;
  }

  /* ── PRODUCT LIST (inside expanded regimen) ──────────── */
  .preset-products-list {
    border-top: 1px solid #EBEBEB;
    padding: 8px 10px;
    background: #FAFAFA;
  }

  /* Inherit the same .product-wrapper card style */
  .preset-products-list .product-wrapper {
    border: 1px solid #EBEBEB;
    margin: 0 0 5px;
    border-radius: 12px;
    background: #fff;
    overflow: hidden;
    cursor: grab;
    transition: box-shadow .18s, border-color .18s;

    &:hover {
      border-color: #9F7ED9;
      box-shadow: 0 4px 18px rgba(159, 126, 217, .13);
    }

    &:active {
      cursor: grabbing;
      opacity: 0.7;
    }
  }

  .preset-products-list .product-item {
    display: flex;
    align-items: center;
    gap: .75rem;
    padding: 10px 20px;
  }

  .preset-products-list .product-info-wrapper {
    display: flex;
    align-items: center;
    gap: .85rem;
    flex: 1;
    min-width: 0;
  }

  .preset-products-list .product-image {
    width: 40px;
    height: 40px;
    border: 1px solid #F2F2F2;
    object-fit: contain;
    flex-shrink: 0;
    border-radius: 6px;
    background: #f3f3f9;
  }

  .preset-products-list .product-details {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .preset-products-list .product-name {
    font-size: 16px;
    font-weight: 400;
    color: #6b7280;
    margin: 0 0 3px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .preset-products-list .product-vendor {
    font-size: 16px;
    font-weight: 700;
    color: #111827;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .preset-products-list .product-actions {
    display: flex;
    align-items: center;
    gap: 32px;
    flex-shrink: 0;
  }

  .preset-products-list .btn-view {
    font-size: 14px;
    font-weight: 600;
    color: #111827;
    text-decoration: underline;
    cursor: pointer;
    white-space: nowrap;
    transition: color .15s;

    &:hover {
      color: #9F7ED9;
    }
  }

  .preset-products-list .time-actions {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  /* Re-use the same .time-btn style from the parent */
  .preset-products-list .time-btn {
    width: 80px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 3px;
    background: #F9F9F9;
    border: 1px solid #E4E4E4;
    border-radius: 8px;
    padding: 4px 12px;
    cursor: pointer;
    color: #374151;
    transition: border-color .15s, color .15s, background .15s;
    line-height: 1;
    white-space: nowrap;

    &:hover {
      border-color: #9F7ED9;
      color: #9F7ED9;
      background: #f5f0fd;
    }
  }

  .preset-products-list .time-btn--both {
    padding: .38rem .55rem;
  }

  /* "Added" badge */
  .product-added-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    font-weight: 600;
    color: #5eb052;
  }

  /* Expandable description */
  .preset-products-list .product-description {
    border-top: 1px solid #EBEBEB;
    padding: .75rem 1rem;
    background: #fafafa;
  }

  .preset-products-list .description-content h5 {
    font-size: 12px;
    font-weight: 700;
    margin-bottom: .4rem;
    color: #333;
  }

  .preset-products-list .description-text {
    font-size: .83rem;
    color: #000;
    line-height: 1.5;
  }

  .preset-products-list .no-description {
    font-size: .83rem;
    color: #222;
    margin: 0;
  }
</style>

<script>
import ApiService, {
  TopPicksService
} from '@/services/api.service';

import ShopifyService from '@/services/shopify';

export default {
  name: 'TopPicksNew',

  props: {
    query: {
      type: String,
      default: ''
    },

    sortKey: {
      type: String,
      default: 'TITLE'
    },

    reverse: {
      type: Boolean,
      default: false
    },

    morningProducts: {
      type: Array,
      default: () => []
    },

    eveningProducts: {
      type: Array,
      default: () => []
    },

    practice: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      sending: false,
      physicians: [],
      expandedProduct: null,
      loadRequestId: 0,
      productsPerPage: 20
    };
  },

  watch: {
    query() {
      this.loadTopPicks();
    },

    sortKey() {
      this.loadTopPicks();
    },

    reverse() {
      this.loadTopPicks();
    },

    practice: {
      immediate: true,
      deep: true,

      handler() {
        this.loadTopPicks();
      }
    }
  },

  methods: {
    async loadTopPicks() {
      if (!this.practice || !this.practice.id) {
        return;
      }

      const requestId = ++this.loadRequestId;

      this.sending = true;

      try {
        if (this.isRole('super_admin')){
          ApiService.practiceId = `/${this.practice.id}`;
        }
        
        /*
         * First get physicians and their Top Pick product IDs.
         */
        const response = await TopPicksService.get();

        if (requestId !== this.loadRequestId) {
          return;
        }

        const physicians =
          response.data.physicians &&
          response.data.physicians.items
            ? response.data.physicians.items
            : [];

        /*
         * Collect all unique Shopify product IDs.
         */
        const productIds = [
          ...new Set(
            physicians.flatMap(physician =>
              (physician.top_picks_products || [])
                .map(product => product.id)
            )
          )
        ];

        /*
         * No Top Pick products.
         */
        if (!productIds.length) {
          this.physicians = physicians.map(physician => ({
            ...physician,
            products: [],
            showProducts: false
          }));

          return;
        }

        /*
         * Fetch all matching Shopify products.
         *
         * ShopifyService handles:
         * - concern
         * - brand
         * - category
         * - ingredient
         * - price
         * - search
         * - sorting
         */
        const products =
          await this.fetchAllTopPickProducts(productIds);

        if (requestId !== this.loadRequestId) {
          return;
        }

        /*
         * Convert GraphQL product IDs to a simple lookup map.
         */
        const productsById = new Map();

        products.forEach(product => {
          const id = this.extractNumericId(product.id);

          productsById.set(id, this.normaliseProduct(product));
        });

        /*
         * Attach Shopify products back to each physician.
         *
         * Top Pick position controls the order.
         */
        this.physicians = physicians.map(physician => {
          const topPicks =
            (physician.top_picks_products || [])
              .slice()
              .sort((a, b) => a.position - b.position);

          const physicianProducts = topPicks
            .map(topPick => {
              const product =
                productsById.get(
                  this.extractNumericId(topPick.id)
                );

              return product;
            })
            .filter(product => product);

          return {
            ...physician,
            products: physicianProducts,
            showProducts: false
          };
        });
      } catch (error) {
        console.error(
          'Error loading Top Picks:',
          error
        );

        if (requestId === this.loadRequestId) {
          this.physicians = [];
        }
      } finally {
        if (requestId === this.loadRequestId) {
          this.sending = false;
        }
      }
    },

    async fetchAllTopPickProducts(ids) {
      let products = [];
      let hasNextPage = true;
      let after = null;

      while (hasNextPage) {
        const params = {
          first: this.productsPerPage,
          query: this.query,
          sortKey: this.sortKey,
          reverse: this.reverse
        };

        if (after) {
          params.after = after;
        }

        const response =
          await ShopifyService.fetchQuery(
            params,
            ids,
            true
          );

        products = products.concat(
          response.edges || []
        );

        hasNextPage =
          response.pageInfo &&
          response.pageInfo.hasNextPage
            ? response.pageInfo.hasNextPage
            : false;

        after =
          response.pageInfo &&
          response.pageInfo.endCursor
            ? response.pageInfo.endCursor
            : null;
      }

      return products;
    },

    /*
     * Shopify GraphQL returns:
     *
     * gid://shopify/Product/123
     *
     * Top Picks API normally gives:
     *
     * 123
     *
     * Normalize both formats.
     */
    extractNumericId(id) {
      return String(id).replace(
        'gid://shopify/Product/',
        ''
      );
    },

    /*
     * Keep the product shape compatible with the
     * product structure used by PresetRegimenNew.
     */
    normaliseProduct(product) {
      const variant =
        product.variants &&
        product.variants.length
          ? product.variants[0]
          : {};

      const image =
        product.images &&
        product.images.length
          ? product.images[0]
          : {};

      return {
        id: product.id,

        title: product.title,

        handle: product.handle || '',

        vendor: product.vendor || '',

        productType:
          product.productType || 'Product',

        descriptionHtml:
          product.descriptionHtml || '',

        variants: [
          {
            id: variant.id,

            title:
              variant.title || '',

            sku:
              variant.sku || '',

            price: {
              amount:
                variant.price &&
                variant.price.amount
                  ? variant.price.amount
                  : 0
            }
          }
        ],

        images: {
          edges: [
            {
              node: {
                url:
                  image.src ||
                  image.url ||
                  ''
              }
            }
          ]
        }
      };
    },

    productImage(product) {
      if (
        product.images &&
        product.images.edges &&
        product.images.edges.length &&
        product.images.edges[0].node
      ) {
        return (
          product.images.edges[0].node.url ||
          '/images/product-placeholder.jpg'
        );
      }

      return '/images/product-placeholder.jpg';
    },

    showProducts(physician) {
      this.$set(
        physician,
        'showProducts',
        !physician.showProducts
      );

      this.$nextTick(() => {
        if (!physician.showProducts) {
          return;
        }

        const card = document.getElementById(
          `top-pick-card-${physician.id}`
        );

        if (card) {
          card.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    },

    toggleProductDetail(productId) {
      this.expandedProduct =
        this.expandedProduct === productId
          ? null
          : productId;
    },

    startDrag(event, product) {
      event.dataTransfer.effectAllowed = 'copy';

      event.dataTransfer.setData(
        'product',
        JSON.stringify(product)
      );

      event.dataTransfer.setData(
        'type',
        'product'
      );

      const dragImage =
        document.createElement('div');

      dragImage.style.cssText = `
        position: absolute;
        top: -1000px;
        left: -1000px;
        background: white;
        border: 2px solid #9F7ED9;
        border-radius: 12px;
        padding: 16px 20px;
        box-shadow: 0 4px 12px rgba(159, 126, 217, 0.3);
        width: 660px;
        min-width: 660px;
        z-index: 10000;
      `;

      dragImage.innerHTML = `
        <div style="
          font-size: 14px;
          font-weight: 700;
          color: #111827;
          margin-bottom: 6px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        ">
          ${product.title}
        </div>

        <div style="
          font-size: 12px;
          color: #6b7280;
          white-space: nowrap;
        ">
          Let go to drop in position
        </div>
      `;

      document.body.appendChild(dragImage);

      event.dataTransfer.setDragImage(
        dragImage,
        330,
        30
      );

      setTimeout(() => {
        if (dragImage.parentNode) {
          dragImage.parentNode.removeChild(
            dragImage
          );
        }
      }, 0);
    },

    emitProduct(product, section) {
      this.$emit(
        'open-preset-product',
        {
          product,
          section
        }
      );
    },

    handleProducts(products, section) {
      products.forEach(product => {
        this.emitProduct(
          product,
          section
        );
      });
    },

    productId(product) {
      return String(
        product.shopify_product_id ||
        product.shopify_product_graph_id ||
        product.id
      ).replace(
        'gid://shopify/Product/',
        ''
      );
    },

    isInMorning(product) {
      if (!this.morningProducts.length) {
        return false;
      }

      const id = this.productId(product);

      return this.morningProducts.some(
        item =>
          this.productId(item) === id
      );
    },

    isInEvening(product) {
      if (!this.eveningProducts.length) {
        return false;
      }

      const id = this.productId(product);

      return this.eveningProducts.some(
        item =>
          this.productId(item) === id
      );
    },

    isPhysicianAllAdded(
      physician,
      section
    ) {
      if (!physician.products.length) {
        return true;
      }

      if (section === 'am') {
        return physician.products.every(
          product =>
            this.isInMorning(product)
        );
      }

      if (section === 'pm') {
        return physician.products.every(
          product =>
            this.isInEvening(product)
        );
      }

      return physician.products.every(
        product =>
          this.isInMorning(product) &&
          this.isInEvening(product)
      );
    }
  }
};
</script>